import { useMemo } from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  Divider,
} from "@mui/material";
import { HomeOwnerMatchingProfile, BudgetRange } from "../../__generated__/types";
import { bugetRangeLabels } from "../../utils/option-helpers";

// Priority label map for proper formatting
const priorityLabelMap: Record<string, string> = {
  'COMPATIBLE_PERSONALITIES': 'Compatible Personalities',
  'BUDGET': 'Budget',
  'TIMELINE': 'Timeline',
  'HOW_DESIGNER_BILLS': 'How the Designer Bills',
  'STYLE': 'Style',
  'PROJECT_FINANCING': 'Project Financing is Offered',
  'DESIGNER_YEARS_EXPERIENCE': 'Designer Years of Experience',
  'COMPATIBLE_PERSONAL_PREFERENCES': 'Compatible Personal Preferences',
  'IDEAL_DESIGNER_TRAITS': 'Ideal Designer Traits',
  'PROJECT_SPECIAL_NEEDS': 'Project Special Needs',
};

// Concept presentation label map for proper formatting
const conceptPresentationLabelMap: Record<string, string> = {
  'boards': 'Mood & Vision Boards',
  'twoD': '2D Layouts',
  'threeD': '3D Layouts',
  'virtual': 'Virtual Walk Through',
  'no_preference': 'No Preference',
};

// Billing method label map
const billingMethodLabelMap: Record<string, string> = {
  'HOURLY': 'Hourly Rate',
  'HOURLY_RATE': 'Hourly Rate',
  'FLATFEE': 'Flat Fee',
  'FLAT_FEE': 'Flat Fee',
  'PERCENTPROJECT': 'Percentage of Project Cost',
  'PERCENT_PROJECT': 'Percentage of Project Cost',
  'PERSQUAREFOOT': 'Per Square Foot Flat Rate',
  'PER_SQUARE_FOOT': 'Per Square Foot Flat Rate',
  'ALLINCLUSIVE': 'All Inclusive Project Retainer',
  'ALL_INCLUSIVE': 'All Inclusive Project Retainer',
  'HYBRID': 'Hybrid',
  'FLEXIBLE': 'Flexible to Design\'s Preference',
};

// Political affiliation label map
const politicalAffiliationLabelMap: Record<string, string> = {
  'NO_PREF': 'No Preference',
  'REPUBLICAN': 'Republican',
  'DEMOCRAT': 'Democrat',
  'LIBERTARIAN': 'Libertarian',
  'INDEPENDENT': 'Independent',
  'OTHER': 'Other',
};

const toTitleCase = (value?: string | null) => {
  if (!value) return "";
  
  // Check priority label map first
  if (priorityLabelMap[value]) {
    return priorityLabelMap[value];
  }
  
  // Check billing method label map
  if (billingMethodLabelMap[value]) {
    return billingMethodLabelMap[value];
  }
  
  // Check political affiliation label map
  if (politicalAffiliationLabelMap[value]) {
    return politicalAffiliationLabelMap[value];
  }
  
  // Handle all-caps strings (e.g., "FULLHOME" -> "Full Home", "HOMEOFFICE" -> "Home Office")
  // But don't split single words like "BUDGET", "TIMELINE", "HOURLY"
  if (value === value.toUpperCase() && value.length > 1 && !value.includes('_') && !value.includes(' ')) {
    // Single word all-caps (like "BUDGET", "TIMELINE", "HOURLY") - just title case it
    if (value.length <= 8 && !/[A-Z]{4,}[A-Z]{4,}/.test(value)) {
      return value.charAt(0) + value.slice(1).toLowerCase();
    }
    
    // Common word patterns to split on (ordered by length, longest first to avoid partial matches)
    const commonWords = ['INTEGRATION', 'ELEMENTS', 'FRIENDLY', 'HANDLES', 'PROJECT', 'PERCENT', 'OFFICE', 'OUTDOOR', 'SMARTHOME', 'SMART', 'HOME', 'SPACE', 'GYM', 'LEADS', 'SERVICE', 'FLAT', 'FEE', 'RATE', 'ALL'];
    
    let result = value;
    
    // First, try to split by common words - check if the string contains or ends with common words
    // Insert spaces before common words that appear in the string
    commonWords.forEach(word => {
      // Match the word at the end or in the middle (but not at the start to avoid double spaces)
      const endRegex = new RegExp(`([A-Z]+)(${word})(?=[A-Z]|$)`, 'gi');
      result = result.replace(endRegex, '$1 $2');
      
      // Also handle if the word is at the start
      const startRegex = new RegExp(`^(${word})([A-Z]+)`, 'gi');
      result = result.replace(startRegex, '$1 $2');
    });
    
    // Then handle remaining sequences by splitting between capital letter groups
    // This handles cases like "FULLHOME" where we need to split "FULL" and "HOME"
    // Split when we have a transition from one capital sequence to another
    result = result
      .replace(/([A-Z]{3,})([A-Z]{3,})/g, '$1 $2') // Split between groups of 3+ capitals
      .replace(/([A-Z]{2})([A-Z]{2,})/g, (match, p1, p2) => {
        // Only split if p2 is a common word or if it's clearly a different word
        for (const word of commonWords) {
          if (p2.startsWith(word) || p2 === word) {
            return `${p1} ${p2}`;
          }
        }
        // If p1 is a common word, split
        for (const word of commonWords) {
          if (p1 === word) {
            return `${p1} ${p2}`;
          }
        }
        return match;
      })
      .trim();
    
    // If no splitting occurred, just title case the single word
    if (result === value) {
      return value.charAt(0) + value.slice(1).toLowerCase();
    }
    
    // Split by spaces and title case each word
    return result
      .split(/\s+/)
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
  
  // Handle regular camelCase and mixed case
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Split between lowercase and uppercase
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // Split before capital followed by lowercase
    .replace(/_/g, " ") // Replace underscores with spaces
    .replace(/\s+/g, " ") // Collapse multiple spaces
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Format preference values (NOPREF -> No Preference, YES -> Yes, NO -> No)
const formatPreferenceValue = (value?: string | null): string => {
  if (!value) return "";
  const upperValue = value.toUpperCase();
  if (upperValue === 'NOPREF' || upperValue === 'NO_PREFERENCE') {
    return 'No Preference';
  }
  if (upperValue === 'YES' || upperValue === 'TRUE') {
    return 'Yes';
  }
  if (upperValue === 'NO' || upperValue === 'FALSE') {
    return 'No';
  }
  return toTitleCase(value);
};

type SectionItem = {
  label?: string;
  value: string;
};

type Section = {
  title: string;
  items: SectionItem[];
};

const buildSections = (profile?: HomeOwnerMatchingProfile | null) => {

  if (!profile) {
    return [];
  }

  const sections: Section[] = [];

  // Personality
  if (profile.personalityType) {
    sections.push({
      title: "Personality",
      items: [{ value: toTitleCase(profile.personalityType) }],
    });
  } else {
  }

  // Design Style
  if (profile.homeOwnerStyles?.homeOwnerStyle?.length) {
    const styleItems: SectionItem[] = [];
    profile.homeOwnerStyles.homeOwnerStyle.forEach((style) => {
      if (style) {
        styleItems.push({ value: toTitleCase(style) });
      }
    });
    if (styleItems.length) {
      sections.push({
        title: "Design Style",
        items: styleItems,
      });
    }
  } else {
  }

  // Project Type
  if (profile.projectType) {
    const projectItems: SectionItem[] = [];
    
    // Handle projectType - can be single value or array
    const projectTypeValue = profile.projectType.projectType;
    if (projectTypeValue) {
      if (Array.isArray(projectTypeValue)) {
        projectTypeValue.forEach((pt: any) => {
          if (pt) {
            projectItems.push({ label: "Project Type", value: toTitleCase(pt) });
          }
        });
      } else {
        projectItems.push({ label: "Project Type", value: toTitleCase(projectTypeValue as string) });
      }
    }
    
    if (profile.projectType.designItems?.length) {
      profile.projectType.designItems.forEach((item) => {
        if (item) {
          projectItems.push({ label: "Design Items", value: toTitleCase(item) });
        }
      });
    }
    
    if (profile.projectType.specialNeeds?.length) {
      profile.projectType.specialNeeds.forEach((need) => {
        if (need) {
          projectItems.push({ label: "Special Needs", value: toTitleCase(need) });
        }
      });
    }
    
    // Handle conceptPresentation - ensure it's always treated as an array
    const conceptPresentationValue = profile.projectType.conceptPresentation;
    console.log('🔍 Displaying conceptPresentation (raw):', {
      value: conceptPresentationValue,
      type: typeof conceptPresentationValue,
      isArray: Array.isArray(conceptPresentationValue),
      length: Array.isArray(conceptPresentationValue) ? conceptPresentationValue.length : 0,
      stringified: JSON.stringify(conceptPresentationValue),
      isNull: conceptPresentationValue === null,
      isUndefined: conceptPresentationValue === undefined,
      hasProjectType: !!profile.projectType,
      projectTypeKeys: profile.projectType ? Object.keys(profile.projectType) : [],
      // Debug: Check if the property exists but is falsy
      hasConceptPresentationProperty: 'conceptPresentation' in (profile.projectType || {}),
      conceptPresentationValueStrict: conceptPresentationValue,
      // Log the entire projectType to see what we're working with
      fullProjectType: JSON.stringify(profile.projectType, null, 2)
    });
    
    // CRITICAL DEBUG: If we have projectType but conceptPresentation is missing/empty, log a warning
    if (profile.projectType && (!conceptPresentationValue || (Array.isArray(conceptPresentationValue) && conceptPresentationValue.length === 0))) {
      console.error('❌ CRITICAL: conceptPresentation is missing or empty in projectType!', {
        projectTypeExists: !!profile.projectType,
        conceptPresentationValue,
        projectTypeFull: profile.projectType,
        projectTypeStringified: JSON.stringify(profile.projectType, null, 2)
      });
    }
    
    // Convert to array if it's not already an array (defensive handling)
    // CRITICAL: Handle null explicitly - GraphQL might return null even though backend should convert it
    let conceptPresentationArray: string[] = [];
    if (conceptPresentationValue === null || conceptPresentationValue === undefined) {
      console.warn('  ⚠️ conceptPresentation is null/undefined from GraphQL, using empty array');
      conceptPresentationArray = [];
    } else if (Array.isArray(conceptPresentationValue)) {
      conceptPresentationArray = conceptPresentationValue.filter(Boolean) as string[];
      if (conceptPresentationArray.length === 0 && conceptPresentationValue.length > 0) {
        console.warn('  ⚠️ conceptPresentation array had only falsy values, all filtered out');
      }
    } else {
      // If it's a single value, convert to array
      conceptPresentationArray = [String(conceptPresentationValue)].filter(Boolean);
      console.log('  ⚠️ conceptPresentation was not an array, converted to array:', {
        original: conceptPresentationValue,
        converted: conceptPresentationArray
      });
    }
    
    console.log('🔍 Displaying conceptPresentation (processed):', {
      array: conceptPresentationArray,
      length: conceptPresentationArray.length,
      stringified: JSON.stringify(conceptPresentationArray),
      allItems: conceptPresentationArray.map((p: string, i: number) => `${i + 1}. ${p}`).join(', ')
    });
    
    // Display each concept presentation as a separate item
    if (conceptPresentationArray.length > 0) {
      conceptPresentationArray.forEach((presentation: string) => {
        if (presentation) {
          // Use label map for proper formatting, fallback to toTitleCase
          const displayValue = conceptPresentationLabelMap[presentation] || toTitleCase(presentation);
          console.log('  ✅ Adding conceptPresentation item:', {
            raw: presentation,
            display: displayValue
          });
          projectItems.push({ label: "Concept Presentation", value: displayValue });
        }
      });
    } else {
      console.log('  ⚠️ No conceptPresentation values found');
    }
    
    if (profile.projectType.projectAddress) {
      const address = profile.projectType.projectAddress;
      const addressParts = [
        address.streetAddress1,
        address.streetAddress2,
        address.city,
        address.state,
        address.zipCode,
      ].filter(Boolean);
      if (addressParts.length) {
        projectItems.push({ label: "Project Address", value: addressParts.join(", ") });
      }
    }
    
    if (projectItems.length) {
      sections.push({
        title: "Project",
        items: projectItems,
      });
    } else {
    }
  } else {
  }

  // Preferences
  if (profile.preferences) {
    const preferenceItems: SectionItem[] = [];
    
    if (profile.preferences.budgetRange) {
      // Use budget range formatter for user-friendly display
      let budgetLabel = bugetRangeLabels[profile.preferences.budgetRange as BudgetRange];
      
      // If not found in labels, try to format numeric ranges (e.g., "50001 75000" or "50001_75000")
      if (!budgetLabel) {
        const budgetValue = profile.preferences.budgetRange.toString();
        // Check if it's a numeric range format
        const numericRangeMatch = budgetValue.match(/(\d+)[\s_]+(\d+)/);
        if (numericRangeMatch) {
          const min = parseInt(numericRangeMatch[1], 10);
          const max = parseInt(numericRangeMatch[2], 10);
          budgetLabel = `$${min.toLocaleString()} - $${max.toLocaleString()}`;
        } else {
          budgetLabel = toTitleCase(budgetValue);
        }
      }
      
      preferenceItems.push({ label: "Budget Range", value: budgetLabel });
    }
    
    // GraphQL query uses billingMethods (correct field name)
    const billingMethods = (profile.preferences as any).billingMethods;
    if (billingMethods?.length) {
      billingMethods.forEach((model: string) => {
        if (model) {
          // Format billing method (e.g., "FLATFEE" -> "Flat Fee", "HOURLYRATE" -> "Hourly Rate")
          const formattedModel = toTitleCase(model);
          preferenceItems.push({ label: "Billing Methods", value: formattedModel });
        }
      });
    }
    
    // GraphQL query uses wantsFinancing (correct field name)
    const financing = (profile.preferences as any).wantsFinancing;
    if (financing !== null && financing !== undefined) {
      const financingDisplay = typeof financing === 'boolean' 
        ? (financing ? 'Yes' : 'No')
        : formatPreferenceValue(financing);
      preferenceItems.push({ label: "Financing Option", value: financingDisplay });
    }
    
    // GraphQL query uses partnershipStyle (String, not an object)
    const partnershipStyle = (profile.preferences as any).partnershipStyle;
    if (partnershipStyle) {
      preferenceItems.push({ label: "Partnership Style", value: toTitleCase(partnershipStyle) });
    }
    
    // GraphQL query uses designerRole (correct field name)
    const designerRole = (profile.preferences as any).designerRole;
    if (designerRole) {
      preferenceItems.push({ label: "Designer Role", value: toTitleCase(designerRole) });
    }
    
    // GraphQL query uses skillRankings (correct field name)
    const skillSet = (profile.preferences as any).skillRankings;
    if (skillSet?.length) {
      skillSet.forEach((skill: string, index: number) => {
        if (skill) {
          preferenceItems.push({ label: "Designer Skill Set", value: `${index + 1}. ${toTitleCase(skill)}` });
        }
      });
    }
    
    // GraphQL query uses acquisitionPreference (String, not an array)
    const acquisitionPreference = (profile.preferences as any).acquisitionPreference;
    if (acquisitionPreference) {
      preferenceItems.push({ label: "Acquisition Preference", value: toTitleCase(acquisitionPreference) });
    }
    
    // Fallback to TypeScript type fields if GraphQL fields don't exist
    if (profile.preferences.designerGenderPreference) {
      preferenceItems.push({ label: "Designer Gender Preference", value: toTitleCase(profile.preferences.designerGenderPreference) });
    }
    
    if (profile.preferences.designerPetPreference?.length) {
      profile.preferences.designerPetPreference.forEach((pet) => {
        if (pet) {
          preferenceItems.push({ label: "Pet Preference", value: toTitleCase(pet) });
        }
      });
    }
    
    if (profile.preferences.lgbtqFriendly) {
      preferenceItems.push({ label: "LGBTQ Friendly", value: formatPreferenceValue(profile.preferences.lgbtqFriendly) });
    }
    
    if (profile.preferences.womenOwnedFirm) {
      preferenceItems.push({ label: "Women-Owned Firm", value: formatPreferenceValue(profile.preferences.womenOwnedFirm) });
    }
    
    if (profile.preferences.minorityOwnedPreference) {
      preferenceItems.push({ label: "Minority-Owned Preference", value: formatPreferenceValue(profile.preferences.minorityOwnedPreference) });
    }
    
    if (profile.preferences.samePolitics) {
      preferenceItems.push({ label: "Prefers Same Politics", value: formatPreferenceValue(profile.preferences.samePolitics) });
    }
    
    if (profile.preferences.politicalAffiliation) {
      preferenceItems.push({ label: "Political Affiliation", value: toTitleCase(profile.preferences.politicalAffiliation) });
    }
    
    if (profile.preferences.sameAgeRangePreference) {
      preferenceItems.push({ label: "Same Age Range Preference", value: formatPreferenceValue(profile.preferences.sameAgeRangePreference) });
    }
    
    if (profile.preferences.desiredAgeRange) {
      // Format age range (e.g., "6 9" -> "6-9 years", "6_9" -> "6-9 years")
      const ageRange = profile.preferences.desiredAgeRange.toString();
      // Check if it's a numeric range format - handle spaces, underscores, or dashes
      let numericRangeMatch = ageRange.match(/(\d+)\s+(\d+)/); // Space separated
      if (!numericRangeMatch) {
        numericRangeMatch = ageRange.match(/(\d+)[_-](\d+)/); // Underscore or dash separated
      }
      if (numericRangeMatch) {
        const min = numericRangeMatch[1];
        const max = numericRangeMatch[2];
        preferenceItems.push({ label: "Desired Age Range", value: `${min}-${max} years` });
      } else {
        preferenceItems.push({ label: "Desired Age Range", value: toTitleCase(ageRange) });
      }
    }
    
    if (profile.preferences.hasChildrenPreference) {
      preferenceItems.push({ label: "Has Children Preference", value: formatPreferenceValue(profile.preferences.hasChildrenPreference) });
    }
    
    if (profile.preferences.experiencePreference) {
      // Format experience preference (e.g., "3 5" -> "3-5 years", "3_5" -> "3-5 years", "6 9" -> "6-9 years")
      const experience = profile.preferences.experiencePreference.toString();
      // Check if it's a numeric range format - handle spaces, underscores, or dashes
      let numericRangeMatch = experience.match(/(\d+)\s+(\d+)/); // Space separated
      if (!numericRangeMatch) {
        numericRangeMatch = experience.match(/(\d+)[_-](\d+)/); // Underscore or dash separated
      }
      if (numericRangeMatch) {
        const min = numericRangeMatch[1];
        const max = numericRangeMatch[2];
        preferenceItems.push({ label: "Experience Preference", value: `${min}-${max} years` });
      } else {
        preferenceItems.push({ label: "Experience Preference", value: toTitleCase(experience) });
      }
    }
    
    if (preferenceItems.length) {
      sections.push({
        title: "Preferences",
        items: preferenceItems,
      });
    } else {
    }
  } else {
  }

  if (profile.priorities) {
    const priorityItems: SectionItem[] = [];
    
    // Access order array directly, same way preferences accesses its fields
    // IMPORTANT: Preserve the exact order from the array - do not sort or reorder
    const prioritiesOrder = profile.priorities.order;
    if (prioritiesOrder && Array.isArray(prioritiesOrder) && prioritiesOrder.length > 0) {
      // Create a copy of the array to ensure we're working with the exact order
      const orderArray = [...prioritiesOrder];
      
      // Process in the exact order received - do not sort or reorder
      orderArray.forEach((priority, index) => {
        if (priority) {
          // Use priority label map for proper formatting
          const priorityLabel = priorityLabelMap[priority] || toTitleCase(priority);
          priorityItems.push({ value: `${index + 1}. ${priorityLabel}` });
        } else {
          console.log(`Skipping empty priority at index ${index}`);
        }
      });
    }
    
    if (priorityItems.length > 0) {
      sections.push({
        title: "Priorities",
        items: priorityItems,
      });
    } else {
      // Still show section if step was completed, even if no items
      if (profile.stepCompletion?.prioritiesCompleted) {
        console.log('Step completed but no items - adding empty section');
        sections.push({
          title: "Priorities",
          items: [{ value: "No priorities data available" }],
        });
      }
    }
  } else {
    console.log('⚠️ No priorities found');
    // Show section if step was completed, even if priorities object is missing
    if (profile.stepCompletion?.prioritiesCompleted) {
      console.log('⚠️ Step completed but priorities object missing - adding empty section');
      sections.push({
        title: "Priorities",
        items: [{ value: "No priorities data available" }],
      });
    }
  }

  return sections;
};

type SectionType = 'personality' | 'design-style' | 'project' | 'preferences' | 'priorities' | 'all';

interface HomeownerMatchingProfileRetakeSummaryProps {
  profile?: HomeOwnerMatchingProfile | null;
  loading?: boolean;
  currentSection?: SectionType;
}

export const HomeownerMatchingProfileRetakeSummary = ({
  profile,
  loading,
  currentSection = 'all',
}: HomeownerMatchingProfileRetakeSummaryProps) => {

  const sections = useMemo(() => {
    const builtSections = buildSections(profile);
    
    if (currentSection === 'all') {
      return builtSections;
    }
    
    const sectionNameMap: Record<SectionType, string> = {
      'personality': 'Personality',
      'design-style': 'Design Style',
      'project': 'Project',
      'preferences': 'Preferences',
      'priorities': 'Priorities',
      'all': 'All'
    };
    
    const targetSectionName = sectionNameMap[currentSection];
    return builtSections.filter(section => section.title === targetSectionName);
  }, [profile, currentSection]);

  if (loading && !profile) {
    return (
      <Box sx={{ p: 3, mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Loading your previous answers…
        </Typography>
      </Box>
    );
  }

  if (!profile) {
    return null;
  }
  
  if (sections.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          No profile data available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Divider sx={{ mb: 2 }} />
      <Stack spacing={2}>
        {sections.map((section) => (
          <Box key={section.title}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              {section.title}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {section.items.map((item, index) => (
                <Chip
                  key={`${section.title}-${index}`}
                  label={item.label ? `${item.label}: ${item.value}` : item.value}
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
